import React, { useEffect, useState } from 'react';
import { Hash, ChevronRight, MessageCircle, Server, LogIn, Bot, ExternalLink } from 'lucide-react';
import { Card } from '../components/ui';
import { useUIStore, useServerStore, useAuthStore } from '../store';
import { api } from '../lib/api';
import type { ChannelVisit, ChannelGroup } from '../types';

// Group visits by server/DM
function groupChannelVisits(visits: ChannelVisit[]): ChannelGroup[] {
  const groups = new Map<string, ChannelGroup>();

  for (const visit of visits) {
    // DMs, Group DMs, or channels without a guild go into "Direct Messages" group
    if (visit.channelType === 1 || visit.channelType === 3 || !visit.guildId) {
      if (!groups.has('dm')) {
        groups.set('dm', {
          id: 'dm',
          name: 'Direct Messages',
          icon: null,
          type: 'dm',
          channels: [],
        });
      }
      groups.get('dm')!.channels.push(visit);
    }
    // Server channels grouped by guild
    else {
      if (!groups.has(visit.guildId)) {
        groups.set(visit.guildId, {
          id: visit.guildId,
          name: visit.guildName || 'Unknown Server',
          icon: visit.guildIcon,
          type: 'guild',
          channels: [],
        });
      }
      groups.get(visit.guildId)!.channels.push(visit);
    }
  }

  // Sort: servers first (alphabetically), then DMs at bottom
  return Array.from(groups.values()).sort((a, b) => {
    if (a.type === 'dm') return 1;
    if (b.type === 'dm') return -1;
    return a.name.localeCompare(b.name);
  });
}

// Get avatar info for a channel
function getChannelAvatar(visit: ChannelVisit): { type: 'url' | 'initial' | 'hash'; value: string } {
  // Check if this is a DM (type 1) or appears to be a DM (no guild)
  const isDM = visit.channelType === 1 || (!visit.guildId && visit.channelType !== 3);

  // DM - show recipient avatar or initial
  if (isDM) {
    if (visit.recipientAvatar && visit.recipientId) {
      return {
        type: 'url',
        value: `https://cdn.discordapp.com/avatars/${visit.recipientId}/${visit.recipientAvatar}.png?size=48`,
      };
    }
    // Fallback to initial of recipient name or "DM"
    const initial = visit.recipientName?.[0] || visit.channelName?.[0];
    return { type: 'initial', value: initial && initial !== 'T' ? initial : 'DM' };
  }
  // Group DM - show group icon or first initial
  if (visit.channelType === 3) {
    if (visit.groupIcon && visit.channelId) {
      return {
        type: 'url',
        value: `https://cdn.discordapp.com/channel-icons/${visit.channelId}/${visit.groupIcon}.png?size=48`,
      };
    }
    return { type: 'initial', value: visit.channelName?.[0] || 'G' };
  }
  // Server channel - show hash icon
  return { type: 'hash', value: '#' };
}

export function Channels() {
  const { setView } = useUIStore();
  const { channelVisits, setCurrentServer, setCurrentChannel, setChannelVisits } = useServerStore();
  const { isAuthenticated } = useAuthStore();
  const [loading, setLoading] = useState(true);

  // Load channel visits on mount (only if authenticated)
  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    async function loadVisits() {
      try {
        console.log('Fetching channel visits...');
        const response = await api.channelVisits.list();
        console.log('Channel visits response:', response);
        if (response.success && response.data) {
          console.log('Setting channel visits:', response.data.length, 'visits');
          setChannelVisits(response.data);
        } else {
          console.log('No channel visits data or request failed');
        }
      } catch (err) {
        console.error('Failed to load channel visits:', err);
      }
      setLoading(false);
    }
    loadVisits();
  }, [setChannelVisits, isAuthenticated]);

  const handleSelectVisit = (visit: ChannelVisit) => {
    // Convert visit to channel format and navigate
    // Determine display name based on channel type and available data
    let displayName: string;
    if (visit.channelType === 1 || (!visit.guildId && visit.channelType !== 3)) {
      // DM - show recipient name or fallback
      displayName = visit.recipientName || 'Direct Message';
    } else if (visit.channelType === 3) {
      // Group DM
      displayName = visit.channelName || 'Group Chat';
    } else {
      // Server channel
      displayName = visit.channelName || 'Unknown Channel';
    }

    const serverId = visit.guildId || 'dm';
    console.log('Selecting channel visit:', {
      channelId: visit.channelId,
      channelType: visit.channelType,
      displayName,
      guildId: visit.guildId,
      serverId,
    });

    setCurrentChannel({
      id: visit.channelId,
      name: displayName,
      serverId: serverId,
      serverName: visit.guildName || 'Direct Messages',
      predictionCount: 0,
      memberCount: 0,
      // Avatar info
      type: visit.channelType,
      recipientId: visit.recipientId || undefined,
      recipientAvatar: visit.recipientAvatar || undefined,
      groupIcon: visit.groupIcon || undefined,
      guildIcon: visit.guildIcon || undefined,
    });

    // Also set current server context
    if (visit.guildId) {
      setCurrentServer({
        id: visit.guildId,
        name: visit.guildName || 'Unknown Server',
        icon: visit.guildIcon,
        subscription: null,
        settings: {
          allowAnonymous: true,
          defaultDeadlineDays: 7,
          maxPredictionsPerDay: 10,
          scheduledPrompts: [],
        },
        memberCount: 0,
        predictionCount: 0,
      });
    } else {
      setCurrentServer({
        id: 'dm',
        name: 'Direct Messages',
        icon: null,
        subscription: null,
        settings: {
          allowAnonymous: true,
          defaultDeadlineDays: 7,
          maxPredictionsPerDay: 10,
          scheduledPrompts: [],
        },
        memberCount: 0,
        predictionCount: 0,
      });
    }

    setView('feed');
  };

  const groups = groupChannelVisits(channelVisits);

  return (
    <div className="animate-in">
      <h2 className="text-3xl font-bold mb-8 italic tracking-tight text-center">
        Your Channels
      </h2>

      {loading ? (
        <Card className="py-12 text-center">
          <p className="text-sm text-slate-400">Loading...</p>
        </Card>
      ) : !isAuthenticated ? (
        <Card className="py-12 text-center border-dashed">
          <LogIn size={48} className="mx-auto mb-4 text-slate-500 opacity-50" />
          <p className="text-sm font-bold text-slate-500 tracking-wider uppercase">
            Sign in to view your channels
          </p>
          <p className="text-xs text-slate-600 mt-2 mb-4">
            Connect with Discord to see channels you've visited
          </p>
          <button
            onClick={() => window.location.href = '/auth/login'}
            className="px-4 py-2 bg-foretell-accent text-white rounded-lg font-bold text-sm hover:bg-foretell-accent/80 transition-colors"
          >
            Sign in with Discord
          </button>
        </Card>
      ) : groups.length === 0 ? (
        <Card className="py-12 text-center border-dashed">
          <Bot size={48} className="mx-auto mb-4 text-slate-500 opacity-50" />
          <p className="text-sm font-bold text-slate-500 tracking-wider uppercase">
            No channels visited yet
          </p>
          <p className="text-xs text-slate-600 mt-2 mb-6 max-w-sm mx-auto">
            Add Foretell to your Discord server and open it as an Activity in any channel to get started
          </p>
          <a
            href={`https://discord.com/application-directory/${import.meta.env.VITE_DISCORD_CLIENT_ID}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#5865F2] text-white rounded-lg font-bold text-sm hover:bg-[#4752C4] transition-colors"
          >
            <Bot size={18} />
            Add to Discord
            <ExternalLink size={14} />
          </a>
        </Card>
      ) : (
        groups.map((group) => (
          <div key={group.id} className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              {group.type === 'guild' && group.icon ? (
                <img
                  src={`https://cdn.discordapp.com/icons/${group.id}/${group.icon}.png?size=32`}
                  alt=""
                  className="w-8 h-8 rounded-lg"
                />
              ) : group.type === 'dm' ? (
                <div className="w-8 h-8 rounded-lg bg-foretell-accent/20 flex items-center justify-center">
                  <MessageCircle size={16} className="text-foretell-accent" />
                </div>
              ) : (
                <div className="w-8 h-8 rounded-lg bg-foretell-accent/20 flex items-center justify-center text-foretell-accent font-bold">
                  {group.name[0]}
                </div>
              )}
              <h3 className="text-sm font-bold text-slate-300 uppercase tracking-wider">
                {group.name}
              </h3>
              <span className="text-xs text-slate-500">
                {group.channels.length} channel{group.channels.length !== 1 ? 's' : ''}
              </span>
            </div>

            <div className="grid gap-3">
              {group.channels.map((visit) => {
                const avatar = getChannelAvatar(visit);
                // Determine display name based on channel type and available data
                let displayName: string;
                if (visit.channelType === 1 || (!visit.guildId && visit.channelType !== 3)) {
                  // DM - show recipient name or fallback
                  displayName = visit.recipientName || 'Direct Message';
                } else if (visit.channelType === 3) {
                  // Group DM
                  displayName = visit.channelName || 'Group Chat';
                } else {
                  // Server channel
                  displayName = visit.channelName || 'Unknown Channel';
                }

                return (
                  <Card
                    key={visit.channelId}
                    hoverable
                    onClick={() => handleSelectVisit(visit)}
                    className="group"
                  >
                    <div className="flex items-center gap-4">
                      {avatar.type === 'url' ? (
                        <img
                          src={avatar.value}
                          alt=""
                          className="w-12 h-12 rounded-xl"
                        />
                      ) : avatar.type === 'hash' ? (
                        <div className="w-12 h-12 rounded-xl bg-black/40 flex items-center justify-center text-slate-400 group-hover:text-foretell-accent group-hover:bg-foretell-accent/10 transition-colors">
                          <Hash size={24} />
                        </div>
                      ) : (
                        <div className="w-12 h-12 rounded-xl bg-foretell-accent/20 flex items-center justify-center text-foretell-accent font-bold text-lg">
                          {avatar.value}
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="font-bold text-lg">{displayName}</div>
                        <div className="text-xs text-slate-400 flex items-center gap-3 mt-1">
                          {visit.lastVisitedAt && (
                            <span>
                              Last visited {new Date(visit.lastVisitedAt).toLocaleDateString()}
                            </span>
                          )}
                          {visit.visitCount > 1 && (
                            <span className="text-slate-500">
                              {visit.visitCount} visits
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-[9px] font-mono text-slate-600 mt-1">
                          <div className="flex items-center gap-1">
                            <Hash size={10} />
                            <span>{visit.channelId}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Server size={10} />
                            <span>{visit.guildId || 'dm'}</span>
                          </div>
                        </div>
                      </div>
                      <ChevronRight
                        size={20}
                        className="text-white/20 group-hover:text-white transition-colors"
                      />
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
